import { useCallback, useEffect, useRef, useState } from "react";
import { MediaDeviceFailure, Room, RoomEvent, Track, createLocalTracks } from "livekit-client";
import { useContextStateManagement } from "../context/ContextApi";

// Demo Call - in-browser mode (Issue 8 of the prompt/config version-control PRD): a small,
// dedicated hook so AgentConfigPage.js doesn't have to inline LiveKit room/track plumbing.
// Deliberately NOT built on top of the dialer's useCallEngine.js (child-components/dialer/
// engine/useCallEngine.js) - that hook owns a much larger, dialer-specific domain (recent
// calls, notes, contacts, hold/mute/transfer) tied to human-agent SIP calls, and this is a
// different backend contract entirely (POST /api/agentConfig/demo/:practiceId mints a token for
// a room the LiveKit agent joins to run the practice's SUBMITTED draft bundle - no SIP leg is
// ever dialed). The browser mic <-> Room connect/publish/attach shape below mirrors
// useCallEngine.js's startCall/cleanupMedia pattern closely, since that part of the mechanism is
// genuinely the same LiveKit web SDK usage either way.
const DEMO_CALL_STATE = {
  IDLE: "idle",
  CONNECTING: "connecting",
  IN_CALL: "in_call",
  ENDING: "ending",
};

export { DEMO_CALL_STATE };

export function useAgentConfigDemoCall(practiceId) {
  const { startAgentConfigDemoCall, endAgentConfigDemoCall } = useContextStateManagement();

  const [demoCallState, setDemoCallState] = useState(DEMO_CALL_STATE.IDLE);
  const [demoCallError, setDemoCallError] = useState("");
  const [demoCallDuration, setDemoCallDuration] = useState(0);
  const [demoCallVersionId, setDemoCallVersionId] = useState(null);

  const roomRef = useRef(null);
  const localTracksRef = useRef([]);
  const audioElementsRef = useRef([]);
  const roomNameRef = useRef("");
  const timerRef = useRef(null);
  // True only while OUR OWN endDemoCall() is tearing the call down, so the room's own
  // RoomEvent.Disconnected handler (fired for both an intentional and an unexpected
  // disconnect) doesn't redundantly re-run the same cleanup a second time.
  const endingRef = useRef(false);
  const practiceIdRef = useRef(practiceId);

  useEffect(() => {
    practiceIdRef.current = practiceId;
  }, [practiceId]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanupMedia = useCallback(() => {
    if (roomRef.current) {
      try {
        roomRef.current.disconnect();
      } catch (_) {
        // best-effort - the room may already be gone
      }
      roomRef.current = null;
    }

    for (const track of localTracksRef.current) {
      try {
        track.stop();
      } catch (_) {
        // best-effort
      }
    }
    localTracksRef.current = [];

    for (const element of audioElementsRef.current) {
      try {
        element.pause();
        element.remove();
      } catch (_) {
        // best-effort
      }
    }
    audioElementsRef.current = [];
  }, []);

  // Handles the room ending out from under us (the agent hanging up, an unexpected
  // disconnect) - never called for a user-initiated end, see endingRef above.
  const handleUnexpectedDisconnect = useCallback(() => {
    stopTimer();
    cleanupMedia();
    roomNameRef.current = "";
    setDemoCallState(DEMO_CALL_STATE.IDLE);
  }, [cleanupMedia, stopTimer]);

  const startDemoCall = useCallback(async () => {
    if (!practiceIdRef.current || demoCallState !== DEMO_CALL_STATE.IDLE) {
      return;
    }

    setDemoCallState(DEMO_CALL_STATE.CONNECTING);
    setDemoCallError("");
    setDemoCallDuration(0);

    try {
      const response = await startAgentConfigDemoCall(practiceIdRef.current);
      if (!response?.livekitToken || !response?.livekitWsUrl || !response?.roomName) {
        const message =
          typeof response === "string"
            ? response
            : response?.error || "Invalid response from server";
        throw new Error(message);
      }

      roomNameRef.current = response.roomName;
      setDemoCallVersionId(response.submittedVersionId ?? null);

      // Isolated try/catch: getUserMedia (via createLocalTracks) is the ONLY step that can throw
      // these specific DOMException names. Scoping the classification to just this call means a
      // later failure in room.connect()/publishTrack() (a LiveKit ConnectionError, which always
      // sets .name = "ConnectionError", never one of these DOMException names - confirmed by
      // reading livekit-client's source) can never be mislabeled as a microphone problem.
      //
      // createLocalTracks re-throws the browser's raw getUserMedia DOMException unchanged
      // (confirmed in livekit-client's source: `catch (e) { throw e; }` when no deviceId
      // constraint is in play, which is our case). Real dev logs showed the SAME generic
      // "permission" message on every attempt despite the browser's site-permission indicator
      // showing mic access already granted - the previous two-way check (NotAllowedError vs.
      // NotFoundError only) silently collapsed every OTHER real DOMException (most notably
      // NotReadableError, thrown when the mic is already locked by another app/tab/stale call)
      // into the same misleading "permission blocked" message. Using LiveKit's own
      // MediaDeviceFailure.getFailure() classification (the same one they document for exactly
      // this purpose) instead of hand-rolling the name checks keeps this correct as browsers add
      // new error names over time.
      let localTracks;
      try {
        localTracks = await createLocalTracks({ audio: true, video: false });
      } catch (micError) {
        // eslint-disable-next-line no-console
        console.error("Demo Call: getUserMedia failed", micError?.name, micError?.message);
        const failure = MediaDeviceFailure.getFailure(micError);
        if (failure === MediaDeviceFailure.NotFound) {
          throw new Error(
            "No microphone was found on this device. Demo Call (in-browser) needs a working microphone - try the phone callback option below instead, or retry from a device with a mic.",
          );
        }
        if (failure === MediaDeviceFailure.DeviceInUse) {
          throw new Error(
            "Your microphone is already in use by another app or browser tab (or a previous Demo Call that didn't fully close). Close whatever else might be using it - or fully close and reopen this tab - and try again.",
          );
        }
        if (failure === MediaDeviceFailure.PermissionDenied) {
          // NotAllowedError's raw `.message` is literally "Permission denied" in Chrome, which
          // reads as a backend/auth error rather than "grant mic access" - always replace it.
          // Note this can also fire if the OS itself is blocking the browser from the
          // microphone even when the browser's own per-site permission shows granted.
          throw new Error(
            "Microphone access is blocked. Check both your browser's site settings AND your operating system's microphone privacy settings for this browser, then try again.",
          );
        }
        // Unclassified (OverconstrainedError, SecurityError, AbortError, etc.) - show the real
        // error instead of guessing, so this is debuggable without needing devtools open.
        throw new Error(
          `Couldn't access the microphone (${micError?.name || "unknown error"}: ${micError?.message || "no details"}). Check devtools console for more.`,
        );
      }
      localTracksRef.current = localTracks;

      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          const audioElement = track.attach();
          audioElement.autoplay = true;
          document.body.appendChild(audioElement);
          audioElementsRef.current.push(audioElement);
          audioElement.play().catch(() => {});
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          track.detach().forEach((element) => {
            element.pause();
            element.remove();
          });
        }
      });

      // The agent (or the room being deleted server-side) disconnecting is exactly "the demo
      // call ended" - there's no separate SIP leg to distinguish it from, unlike the dialer.
      room.on(RoomEvent.Disconnected, () => {
        if (!endingRef.current) {
          handleUnexpectedDisconnect();
        }
      });

      await room.connect(response.livekitWsUrl, response.livekitToken);
      for (const track of localTracks) {
        await room.localParticipant.publishTrack(track);
      }

      setDemoCallState(DEMO_CALL_STATE.IN_CALL);
      timerRef.current = window.setInterval(() => {
        setDemoCallDuration((current) => current + 1);
      }, 1000);
    } catch (error) {
      // Mic-specific errors are already translated to a clear message above and rethrown as a
      // plain Error - this branch only ever sees that, or a genuine room.connect()/publishTrack()
      // failure (LiveKit token/auth/network), which should show its own real message rather than
      // a generic mic-access hint.
      // eslint-disable-next-line no-console
      console.error("Demo Call failed", error?.name, error?.message);
      setDemoCallError(
        error?.message || "Couldn't start the demo call. Please try again.",
      );
      cleanupMedia();
      stopTimer();
      roomNameRef.current = "";
      setDemoCallState(DEMO_CALL_STATE.IDLE);
    }
  }, [cleanupMedia, demoCallState, handleUnexpectedDisconnect, startAgentConfigDemoCall, stopTimer]);

  const endDemoCall = useCallback(async () => {
    if (endingRef.current || demoCallState === DEMO_CALL_STATE.IDLE) {
      return;
    }

    endingRef.current = true;
    setDemoCallState(DEMO_CALL_STATE.ENDING);
    const roomName = roomNameRef.current;

    // Disconnect locally first so the mic/browser side stops immediately even if the room
    // cleanup request below is slow or fails.
    cleanupMedia();
    stopTimer();

    try {
      if (roomName && practiceIdRef.current) {
        await endAgentConfigDemoCall(practiceIdRef.current, roomName);
      }
    } catch (error) {
      // Non-fatal - the room may already be gone (agent-side shutdown, browser tab closed
      // earlier, etc.) - still reset local UI state below either way.
      console.error("Failed to end Agent Config demo call cleanly", error);
    } finally {
      roomNameRef.current = "";
      endingRef.current = false;
      setDemoCallState(DEMO_CALL_STATE.IDLE);
    }
  }, [cleanupMedia, demoCallState, endAgentConfigDemoCall, stopTimer]);

  // Best-effort cleanup if the component unmounts mid-call (e.g. navigating away from Agent
  // Config while a demo call is active) - mirrors useCallEngine.js's own unmount guard.
  useEffect(() => {
    return () => {
      stopTimer();
      cleanupMedia();
      if (roomNameRef.current && practiceIdRef.current) {
        endAgentConfigDemoCall(practiceIdRef.current, roomNameRef.current).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    demoCallState,
    demoCallError,
    demoCallDuration,
    demoCallVersionId,
    startDemoCall,
    endDemoCall,
  };
}
